import React from 'react';
import { usePersonaChatUI } from '../../contexts/PersonaChatUIContext';

// Task 28 — `create_artifacts` tool-result renderer.
//
// Renders the batch result as a list of rows, one per artifact the
// persona attempted. ✔ = created (artifact_id available), ⏳ =
// rate_limited (pending retry window), ✕ = error.
//
// Title click target is Wave 7 Task 29's PersonaContextPanel Tools tab
// — this component only emits the intent via `onArtifactSelect`; the
// Panel mount will register the handler. If no handler is wired, the
// row renders as a plain text row (no click affordance), so the
// bubble remains useful even before the Panel lands.

interface ArtifactRow {
  success?: boolean;
  artifact_id?: string;
  title?: string;
  name?: string;
  type?: string;
  status?: string;          // 'created' | 'rate_limited' | 'error'
  error?: string;
  retry_after_s?: number;
}

interface CreateArtifactsResultShape {
  created?: ArtifactRow[];
  rate_limited?: ArtifactRow[];
  errors?: ArtifactRow[];
  results?: ArtifactRow[];
  artifacts?: ArtifactRow[];
}

interface Props {
  result: CreateArtifactsResultShape;
  onArtifactSelect?: (artifactId: string) => void;
}

function rowIcon(status: string | undefined, success: boolean | undefined): string {
  if (status === 'rate_limited') return '⏳';
  if (status === 'error' || success === false) return '✕';
  return '✔';
}

function rowColor(status: string | undefined, success: boolean | undefined): string {
  if (status === 'rate_limited') return 'text-amber-500 dark:text-amber-300';
  if (status === 'error' || success === false) return 'text-rose-500 dark:text-rose-300';
  return 'text-emerald-600 dark:text-emerald-400';
}

function normalizeRows(result: CreateArtifactsResultShape): ArtifactRow[] {
  // Order: created first, then rate_limited, then errors — lets the
  // happy path read at the top and exceptions bunch at the bottom.
  const rows: ArtifactRow[] = [];
  if (result.artifacts) return result.artifacts;
  if (result.results) return result.results;
  if (result.created) rows.push(...result.created.map((r) => ({ ...r, status: r.status ?? 'created', success: true })));
  if (result.rate_limited) rows.push(...result.rate_limited.map((r) => ({ ...r, status: 'rate_limited', success: false })));
  if (result.errors) rows.push(...result.errors.map((r) => ({ ...r, status: 'error', success: false })));
  return rows;
}

export const CreateArtifactsResult: React.FC<Props> = ({ result, onArtifactSelect }) => {
  const rows = normalizeRows(result);
  // Context-provided handler (Task 29 — Panel's reverse-lookup +
  // scroll) takes precedence over the explicit prop, so the bubble
  // doesn't have to prop-drill from MessageList all the way down.
  const { onArtifactSelect: ctxOnArtifactSelect } = usePersonaChatUI();
  const effectiveHandler = onArtifactSelect ?? ctxOnArtifactSelect;
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-col gap-0.5 text-[11px] font-mono">
      {rows.map((row, i) => {
        const title = row.title ?? row.name ?? '(untitled)';
        const icon = rowIcon(row.status, row.success);
        const color = rowColor(row.status, row.success);
        const clickable = Boolean(row.artifact_id) && Boolean(effectiveHandler);
        const hasId = Boolean(row.artifact_id);
        return (
          <div key={row.artifact_id ?? `row-${i}`} className="flex items-baseline gap-1.5 pl-3 border-l border-gray-500/30">
            <span className={color}>{icon}</span>
            {row.type && <span className="opacity-60">{row.type}:</span>}
            {clickable ? (
              <button
                type="button"
                onClick={() => effectiveHandler!(row.artifact_id!)}
                className="truncate text-left underline-offset-2 hover:underline max-w-[22rem]"
              >
                {title}
              </button>
            ) : (
              <span className="truncate max-w-[22rem]">{title}</span>
            )}
            {/* New-tab link to the artifact detail page — complements
                the in-app panel click above. Keeps the chat-as-
                conversation flow intact (single-click still opens the
                side panel) while letting an operator pop the full
                artifact view into a fresh tab if they need to work
                against it. */}
            {hasId && (
              <a
                href={`/artifacts/${row.artifact_id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Open artifact in new tab"
                className="opacity-60 hover:opacity-100 text-[10px]"
              >
                ↗
              </a>
            )}
            {row.status === 'rate_limited' && row.retry_after_s != null && (
              <span className="opacity-60">(retry ~{Math.round(row.retry_after_s / 60)}min)</span>
            )}
            {row.status === 'error' && row.error && (
              <span className="opacity-70 italic">— {row.error}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CreateArtifactsResult;
